import { useEffect, useMemo, useState } from "react";
import TabsWithBadge from "V2.0/common/tabsWithBadge";
import { PromptSettings } from "V2.0/HomePages/Assistants/ModelSettings";
import { VoiceAssistantTools } from "V2.0/HomePages/Assistants/AssistantTools/VoiceAssistantTools";
import { VoiceAssistantSettings } from "V2.0/HomePages/Assistants/AssistantSettings/VoiceAssistantSettings";
import { Transcription } from "V2.0/HomePages/Assistants/TranscriptionSettings/Transcription";
import { TTS } from "V2.0/HomePages/Assistants/TTS/TTSCategorized/TTS";
import { useNavigate, useOutletContext } from "react-router-dom";

import {
  Cog8ToothIcon,
  DevicePhoneMobileIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  WrenchIcon,
} from "@heroicons/react/24/outline";
import { VoiceCommunicationBinding } from "V2.0/HomePages/Assistants/CommunicationBinding/VoiceCommunicationBinding";
import { KnowledgeBase } from "V2.0/HomePages/Assistants/RAG/KnowledgeBase/KnowledgeBase";
import { getVoices } from "V2.0/HomePages/VoicesLibrary/service";
import { message } from "antd";
import { useAssistants } from "V2.0/HomePages/Assistants";
import { useWhatsappTemplates } from "V2.0/HomePages/Integrations/Whatsapp/models/useWhatsappTemplates";
import GPTResponsePremiumModel from "V2.0/HomePages/Assistants/ModelSettings/GPTResponseModelCategorized/GPTResponsePremiumModel";
import TabsWithPills from "V2.0/common/tabsWithPills";
import { PhoneArrowUpRightIcon } from "@heroicons/react/16/solid";
import VoiceWidget from "V2.0/HomePages/Widget/VoiceWidget";
import useMCubeEngageWhatsappTemplates from "V2.0/HomePages/Integrations/Communications/Whatsapp/MCubeEngage/useMCubeEngageWhatsappTemplates";

enum TABS {
  PROMPT = "Prompt",
  TOOLS = "Tools",
  SETTINGS = "Settings",
  KNOWLEDGE = "Knowledge Base",
  PHONE_NUMBERS = "Phone Numbers",
  WIDGET = "Widget",
}

const tabsMap = {
  [TABS.PROMPT]: (props) => <PromptSettings {...props} />,
  [TABS.KNOWLEDGE]: (props) => <KnowledgeBase {...props} />,
  [TABS.SETTINGS]: (props) => <VoiceAssistantSettings {...props} />,
  [TABS.TOOLS]: (props) => <VoiceAssistantTools {...props} />,
  [TABS.PHONE_NUMBERS]: (props) => <VoiceCommunicationBinding {...props} />,
  [TABS.WIDGET]: (props) => <VoiceWidget {...props} />,
};

const TAB_ICON = {
  [TABS.PROMPT]: DocumentDuplicateIcon,
  [TABS.KNOWLEDGE]: FolderIcon,
  [TABS.SETTINGS]: Cog8ToothIcon,
  [TABS.TOOLS]: WrenchIcon,
  [TABS.PHONE_NUMBERS]: DevicePhoneMobileIcon,
  [TABS.WIDGET]: PhoneArrowUpRightIcon,
};

function useConfigureTabs(configurationSections) {
  const [selectedTab, setSelectedTab] = useState(null);

  const activeTab = selectedTab
    ? selectedTab
    : configurationSections.length > 0
      ? configurationSections[0]
      : null;

  const tabs = useMemo(() => {
    if (!configurationSections.length) return [];

    return configurationSections.reduce((acc, tab) => {
      const Icon = TAB_ICON[tab];
      return [
        ...acc,
        {
          name: tab,
          href: "#",
          current: tab === activeTab,
          icon: <Icon className="size-5" />,
        },
      ];
    }, []);
  }, [configurationSections, activeTab]);

  return {
    activeTab,
    tabs,
    setActiveTab: setSelectedTab,
  };
}

export default function VoiceAssistantConfigure(props) {
  const {
    workspaceId,
    assistantId,
    ttsSettings,
    transcriptionSettings,
    modelSettings,
    syncAssistant,
    company,
    locked,
    configurationSections,
  } = props;
  const { projectId } = useOutletContext<{
    projects: [];
    activeProject: object;
    projectId: string;
  }>();

  const [voices, setVoices] = useState([]);
  const { assistants, fetchAssistants } = useAssistants(workspaceId);
  const { templates: whatsappTemplates } = useWhatsappTemplates(workspaceId);
  const { templates: mcubeEngageTemplates } =
    useMCubeEngageWhatsappTemplates(workspaceId);
  const { activeTab, setActiveTab, tabs } = useConfigureTabs(
    configurationSections,
  );

  async function fetchVoices() {
    try {
      const response = await getVoices();
      if (response.status === 200) {
        setVoices(response.data.voices);
      }
    } catch (e) {
      message.error(
        e.response.data?.msg ||
          e.response.data?.message ||
          "Failed to get voices",
      );
    }
  }

  useEffect(() => {
    if (workspaceId) {
      fetchVoices();
      fetchAssistants();
    }
  }, [workspaceId]);

  return (
    <>
      <div className="flex justify-between p-4">
        <div className="flex gap-x-2">
          <TTS
            voices={voices}
            settings={ttsSettings}
            syncAssistant={syncAssistant}
            workspaceId={workspaceId}
            assistantId={assistantId}
            locked={locked}
            showPremiumOptions={
              company?.voiceLibrarySettings?.elevenlabsVoicesEnabled
            }
          />

          <Transcription
            settings={transcriptionSettings}
            syncAssistant={syncAssistant}
            workspaceId={workspaceId}
            assistantId={assistantId}
            locked={locked}
          />
        </div>

        <GPTResponsePremiumModel
          settings={modelSettings}
          syncAssistant={syncAssistant}
          workspaceId={workspaceId}
          assistantId={assistantId}
          showPremiumOptions={
            company?.thirdPartyIntegrationVisibility?.premiumModelVisibility
          }
          enablePreimumOptions={
            company?.thirdPartyIntegrationVisibility?.premiumModelsSelectable
          }
          locked={locked}
          audioDataResidency={props.voiceSettings?.audioDataResidency}
        />
      </div>

      <div className="px-4">
        <TabsWithPills
          tabs={tabs}
          className=""
          onTabChange={(e, { tab, index }) => setActiveTab(tab.name)}
        />
      </div>

      {activeTab ? (
        tabsMap[activeTab]({
          ...props,
          projectId,
          assistants,
          whatsappTemplates: whatsappTemplates.data,
          mcubeEngageTemplates: mcubeEngageTemplates.data,
        })
      ) : (
        <div className="flex-1 flex items-center justify-center m-4 mb-8 border rounded-lg">
          <p className="text-gray-500 text-md">No sections</p>
        </div>
      )}
    </>
  );
}
